import { Injectable, inject, PLATFORM_ID, signal, computed, effect } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { environment } from '../../../environments/environment';

interface ConsentState {
  analytics: boolean;
}

const CONSENT_COOKIE = 'mv_consent';
const CONSENT_MAX_AGE = 60 * 60 * 24 * 365; // 365 days

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

@Injectable({ providedIn: 'root' })
export class ConsentService {
  private readonly doc = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly _state = signal<ConsentState | null>(null);
  private readonly _showManagePanel = signal(false);

  /** True if user has made a consent choice (accept or reject) */
  readonly hasResponded = computed(() => this._state() !== null);

  /** True only if user explicitly accepted analytics */
  readonly analyticsConsented = computed(() => this._state()?.analytics === true);

  /** True when the manage preferences panel should be shown (from footer link) */
  readonly showManagePanel = this._showManagePanel.asReadonly();

  constructor() {
    if (!this.isBrowser) return;

    // 1. Set Google Consent Mode v2 defaults BEFORE gtag.js loads
    this.setConsentDefaults();

    // 2. Read any existing consent from cookie
    const saved = this.readCookie();
    if (saved) {
      this._state.set(saved);
      if (saved.analytics) {
        this.updateGtagConsent(true);
      }
    }

    // 3. Load third-party scripts when analytics consent is granted
    effect(() => {
      if (this.analyticsConsented()) {
        this.loadClarity();
      }
    });
  }

  acceptAll(): void {
    const state: ConsentState = { analytics: true };
    this._state.set(state);
    this.writeCookie(state);
    this.updateGtagConsent(true);
    this.logConsentEvent('accept_all', true);
  }

  rejectAll(): void {
    const state: ConsentState = { analytics: false };
    this._state.set(state);
    this.writeCookie(state);
    this.updateGtagConsent(false);
    this.logConsentEvent('essentials_only', false);
  }

  savePreferences(analytics: boolean): void {
    const state: ConsentState = { analytics };
    this._state.set(state);
    this.writeCookie(state);
    this.updateGtagConsent(analytics);
    this.logConsentEvent('save_preferences', analytics);
  }

  openManagePanel(): void {
    this._showManagePanel.set(true);
  }

  closeManagePanel(): void {
    this._showManagePanel.set(false);
  }

  // ── Private ───────────────────────────────────────────────

  private setConsentDefaults(): void {
    const win = this.doc.defaultView;
    if (!win) return;

    // Ensure dataLayer + gtag function exist before gtag.js loads
    win.dataLayer = win.dataLayer || [];
    if (typeof win.gtag !== 'function') {
      (win as Window & { gtag: (...args: unknown[]) => void }).gtag = function () {
        win.dataLayer.push(arguments);
      };
    }

    win.gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      wait_for_update: 500,
    });
  }

  private updateGtagConsent(granted: boolean): void {
    const win = this.doc.defaultView;
    if (!win || typeof win.gtag !== 'function') return;

    win.gtag('consent', 'update', {
      analytics_storage: granted ? 'granted' : 'denied',
    });
  }

  private writeCookie(state: ConsentState): void {
    const value = encodeURIComponent(JSON.stringify(state));
    this.doc.cookie = `${CONSENT_COOKIE}=${value};path=/;max-age=${CONSENT_MAX_AGE};SameSite=Lax`;
  }

  private readCookie(): ConsentState | null {
    const match = this.doc.cookie.match(new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE}=([^;]*)`));
    if (!match) return null;
    try {
      return JSON.parse(decodeURIComponent(match[1]));
    } catch {
      return null;
    }
  }

  private logConsentEvent(action: string, analyticsGranted: boolean): void {
    const win = this.doc.defaultView;
    if (!win) return;

    const width = win.innerWidth;
    const deviceType = width < 480 ? 'mobile' : width < 1024 ? 'tablet' : 'desktop';

    fetch(`${environment.apiUrl}/logConsent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action,
        analyticsGranted,
        pageUrl: win.location.pathname,
        deviceType,
      }),
    }).catch(() => { /* fire-and-forget */ });
  }

  private clarityLoaded = false;

  private loadClarity(): void {
    if (this.clarityLoaded) return;
    this.clarityLoaded = true;

    const win = this.doc.defaultView;
    if (!win) return;

    // Microsoft Clarity project ID — replace with actual ID when available
    const clarityId = 'YOUR_CLARITY_ID';
    if (clarityId === 'YOUR_CLARITY_ID') return; // Skip until configured

    const script = this.doc.createElement('script');
    script.async = true;
    script.textContent = `
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window,document,"clarity","script","${clarityId}");
    `;
    this.doc.head.appendChild(script);
  }
}
